import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getSupabase, getProfile, createProfile, updateProfile, getBlocks, updateBlocks } from '../lib/supabase';
import { publishToIPFS } from '../lib/ipfs';

const ProfileContext = createContext();

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within ProfileProvider');
  }
  return context;
};

export const ProfileProvider = ({ children }) => {
  const { user, walletAddress } = useAuth();
  const [profile, setProfile] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    if (user) {
      loadProfile();
    } else {
      setProfile(null);
      setBlocks([]);
    }
  }, [user]);

  const loadProfile = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const { data: profileData, error: profileError } = await getProfile(user.id);
      if (profileError && profileError.code === 'PGRST116') {
        // Profile doesn't exist, create one
        const newProfile = {
          user_id: user.id,
          username: `user_${Date.now()}`,
          display_name: '',
          bio: '',
          avatar: '',
          wallet_address: walletAddress,
          theme: 'minimal-light',
          is_public: true,
        };
        const { data: createdProfile } = await createProfile(newProfile);
        setProfile(createdProfile);
        setBlocks([]);
      } else if (profileData) {
        setProfile(profileData);
        // Load blocks
        const { data: blocksData } = await getBlocks(profileData.id);
        setBlocks(blocksData || []);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async (profileData) => {
    if (!user) return;
    try {
      setLoading(true);
      const { data } = await updateProfile(user.id, profileData);
      setProfile(data);
      return { success: true };
    } catch (error) {
      console.error('Error saving profile:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const saveBlocks = async (newBlocks) => {
    if (!profile) return;
    try {
      setLoading(true);
      await updateBlocks(profile.id, newBlocks);
      setBlocks(newBlocks);
      return { success: true };
    } catch (error) {
      console.error('Error saving blocks:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const publishProfile = async () => {
    if (!profile || !user) {
      return { success: false, error: 'User profile not loaded.' };
    }
    setPublishing(true);
    try {
      // Construct profile JSON per PRD spec
      const profileJSON = {
        name: profile.display_name || profile.username,
        username: profile.username,
        bio: profile.bio || '',
        avatar: profile.avatar || '',
        links: [], // TODO: Extract links from blocks if needed
        blocks: blocks.map(block => ({
          id: block.id,
          type: block.type,
          data: block.data,
          order_index: block.order_index
        })),
        theme: profile.theme || 'minimal-light',
        createdAt: profile.created_at || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        wallet: walletAddress || profile.wallet_address || '',
        ens: profile.ens || '',
        version: '2.0'
      };

      // Publish to IPFS with metadata tagging
      const { cid, error } = await publishToIPFS(profileJSON, {
        username: profile.username,
        wallet: walletAddress || profile.wallet_address,
        env: import.meta.env.MODE === 'production' ? 'prod' : 'dev'
      });

      if (error) {
        console.error('Failed to publish to IPFS:', error);
        return { success: false, error: error };
      }

      // Store CID in database
      await updateProfile(user.id, { ipfs_cid: cid });
      setProfile(prev => ({ ...prev, ipfs_cid: cid }));
      
      // Create record in publishes table
      const supabase = getSupabase();
      await supabase.from('publishes').insert({
        profile_id: profile.id,
        ipfs_cid: cid,
        profile_json: profileJSON,
      });
      
      console.log('✅ Profile published to IPFS:', cid);
      return { success: true, cid };
    } catch (error) {
      console.error('An unexpected error occurred during publishing:', error);
      return { success: false, error: error.message };
    } finally {
      setPublishing(false);
    }
  };

  const value = {
    profile,
    blocks,
    loading,
    publishing,
    loadProfile,
    saveProfile,
    saveBlocks,
    publishProfile,
    setBlocks,
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};